import { useEffect } from 'react';

const SEO = ({ title = '', description = '', keywords, canonicalUrl, schema, ogImage, faqs, reviews, breadcrumbs }) => {
  useEffect(() => {
    if (title) document.title = title;

    const setMeta = (attr, attrValue, content) => {
      let el = document.querySelector(`meta[${attr}="${attrValue}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, attrValue);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    if (description) {
      setMeta('name', 'description', description);
      setMeta('property', 'og:description', description);
      setMeta('name', 'twitter:description', description);
    }
    if (title) {
      setMeta('property', 'og:title', title);
      setMeta('name', 'twitter:title', title);
    }
    if (keywords) setMeta('name', 'keywords', keywords);
    setMeta('property', 'og:type', 'website');
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('property', 'og:site_name', 'Total Quality Insurance');

    if (canonicalUrl) {
      setMeta('property', 'og:url', canonicalUrl);
      setMeta('name', 'twitter:url', canonicalUrl);
    }

    const image = ogImage || 'https://totalqualityinsurance.com/og-image.png';
    setMeta('property', 'og:image', image);
    setMeta('name', 'twitter:image', image);
    setMeta('property', 'og:image:width', '1200');
    setMeta('property', 'og:image:height', '630');
    setMeta('property', 'og:image:alt', title || 'Total Quality Insurance');

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonicalUrl) {
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.appendChild(canonical);
      }
      canonical.setAttribute('href', canonicalUrl);
    } else if (canonical) {
      canonical.remove();
    }

    // Build all schemas
    const allSchemas = [];

    // Primary schema (page-specific)
    if (schema) {
      if (schema['@graph']) {
        allSchemas.push(schema);
      } else {
        allSchemas.push(schema);
      }
    }

    // FAQPage schema from faqs array
    if (faqs && faqs.length > 0) {
      allSchemas.push({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map(faq => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer
          }
        }))
      });
    }

    // Review + AggregateRating schema
    if (reviews && reviews.length > 0) {
      const avgRating = reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length;
      allSchemas.push({
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "name": "Total Quality Insurance",
        "@id": "https://totalqualityinsurance.com/#business",
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": avgRating.toFixed(1),
          "bestRating": "5",
          "worstRating": "1",
          "reviewCount": String(reviews.length)
        },
        "review": reviews.slice(0, 10).map(r => ({
          "@type": "Review",
          "author": { "@type": "Person", "name": r.name },
          "reviewRating": {
            "@type": "Rating",
            "ratingValue": String(r.rating),
            "bestRating": "5"
          },
          "reviewBody": r.text
        }))
      });
    }

    // BreadcrumbList schema
    if (breadcrumbs && breadcrumbs.length > 0) {
      allSchemas.push({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": breadcrumbs.map((crumb, i) => ({
          "@type": "ListItem",
          "position": i + 1,
          "name": crumb.name,
          ...(crumb.url ? { "item": crumb.url } : {})
        }))
      });
    }

    // Inject all schemas
    const schemaId = 'seo-schema-markup';
    let schemaEl = document.getElementById(schemaId);
    if (allSchemas.length > 0) {
      const schemaContent = allSchemas.length === 1 ? allSchemas[0] : allSchemas;
      if (!schemaEl) {
        schemaEl = document.createElement('script');
        schemaEl.id = schemaId;
        schemaEl.type = 'application/ld+json';
        document.head.appendChild(schemaEl);
      }
      schemaEl.textContent = JSON.stringify(schemaContent);
    } else if (schemaEl) {
      schemaEl.remove();
    }

    return () => {
      const el = document.getElementById(schemaId);
      if (el) el.remove();
    };
  }, [title, description, keywords, canonicalUrl, schema, faqs, reviews, breadcrumbs]);

  return null;
};

export default SEO;
